import { Container, createRoot } from "react-dom/client";
import { createBrowserRouter, RouterProvider, Outlet, useLocation } from "react-router-dom";
import { useEffect, useRef } from "react";
import "../index.css";
import Landing from "./components/Landing";
import ProjectsPage from "./components/Projects";
import TravelPage from "./components/Travel";
import EssayPage from "./components/Essay";
import TravelArticlePage from "./components/TravelArticle";

// Keep in sync with the <title> in each route's HTML entry (index.html, projects.html, …)
const PAGE_TITLES: Record<string, string> = {
  "/": "Megholova — Software Engineer & Wanderer",
  "/projects": "Projects — Megholova",
  "/travel": "Travel — Megholova",
  "/essay": "The Long Way Around — Megholova",
};

// Wraps every route — handles fx-in/fx-out across client-side navigations
const SiteShell = () => {
  const ref = useRef<HTMLDivElement>(null);
  const location = useLocation();

  // The HTML entry sets the title on first load; update it on client-side navigations
  useEffect(() => {
    const title = PAGE_TITLES[location.pathname];
    if (title) document.title = title;
  }, [location.pathname]);

  // Re-run on every route change: strip fx-out left by transitionTo, then fade in
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.classList.remove("fx-out");
    requestAnimationFrame(() => el.classList.add("fx-in"));
  }, [location.pathname]);

  // bfcache restore
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        el.classList.remove("fx-out");
        el.classList.add("fx-in");
      }
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  // Scroll to top on route change (unless navigating to a hash)
  useEffect(() => {
    if (!location.hash) window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="site" ref={ref}>
      <Outlet />
    </div>
  );
};

const router = createBrowserRouter([
  {
    element: <SiteShell />,
    children: [
      { path: "/", element: <Landing /> },
      { path: "/projects", element: <ProjectsPage /> },
      { path: "/travel", element: <TravelPage /> },
      { path: "/travel/:slug", element: <TravelArticlePage /> },
      { path: "/essay", element: <EssayPage /> },
    ],
  },
]);

const root = createRoot(document.getElementById("root") as Container);
root.render(<RouterProvider router={router} />);
